// THE single read/write module for the persona-based onboarding config.
//
// Every surface that reads or writes the config goes through this file —
// never through the slice directly.
//
// The server owns a client user's config. It arrives in the
// `/user_preferences` GET the app root issues once per load, and every write
// PUTs it back. Nothing of theirs is persisted in the browser, so a second
// device sees the same setup and a cleared profile loses nothing.
//
// A Dashmote staff member's answers go the other way: they stay in this
// tab's session and the server never hears about them, so logging out
// returns them to the Power User default they hold in every workspace.
// `utils/staffPersonaSession.ts` carries that reasoning.
//
// uid resolution is the one impersonation-aware read site: `state.auth.user`
// is overwritten with the target identity during "View as User"
// (authSlice.setImpersonationActive), so resolving the uid from there means
// an admin viewing as a user reads the TARGET's config, never their own —
// which resolves to absent, because no second fetch runs after the identity
// swap and the slice still holds the admin's. Every write is refused while
// impersonating: "View as User" is a read-only lens.
//
// Reads also judge the config's usability before handing it out, so a shape
// no reader can dereference resolves to absent and re-runs onboarding.

import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  nextExtras,
  setPersonaConfig,
  setStaffSession,
  toggleExtra as toggleExtraAction,
} from "../slices/personaConfigSlice";
import type { PersonaLoadStatus } from "../slices/personaConfigSlice";
import { ALL_PERSONA_IDS } from "../data/personaDefinitions";
import { isDashmoteStaffUser } from "../slices/authSlice";
import { savePersonaConfig } from "../api/userPreferencesApi";
import { refreshNavigation, toggleCachedExtra } from "@dashai/lib/navigationCache";
import type { PersonaConfigBody } from "../api/userPreferencesApi";
import type {
  ExtraKind,
  PersonaConfig,
} from "../data/personaConfigTypes";
import {
  EMPTY_STAFF_PERSONA_SESSION,
  readStaffPersonaWorkspaces,
  writeStaffPersonaSession,
} from "../utils/staffPersonaSession";
import type {
  StaffPersonaSession,
  StaffPersonaWorkspaces,
} from "../utils/staffPersonaSession";

interface StateWithPersonaConfig {
  auth?: {
    user?: { uid?: string | null } | null;
    isImpersonating?: boolean;
    selectedWorkspace?: { key?: string } | null;
    id_client?: string | null;
    // The fetched profile, which carries the address and account type the
    // staff predicate needs. ProtectedRoute's Gate 3 holds the app on a
    // spinner until this is populated, so the staff default below is never
    // decided against a half-loaded profile.
    userData?: { email?: string | null; account_type?: string | null } | null;
  };
  personaConfig?: {
    configsByUid?: Record<string, unknown>;
    status?: PersonaLoadStatus;
    staffSession?: StaffPersonaSession;
  };
}

// The shape this module writes and every reader dereferences. A mismatch is
// what makes a stored config unusable rather than merely stale.
//
// Exported for the shared test helper, which injects a default config into
// every test that mounts a protected surface. Hard-coding the number there
// instead let it drift, and the failure surfaced far from the cause: a stale
// version reads as unusable, so Gate 5 redirected and unrelated suites failed
// on a missing "PROTECTED CONTENT".
export const PERSONA_CONFIG_SCHEMA_VERSION = 2;

const selectCurrentUid = (state: StateWithPersonaConfig): string | null =>
  state.auth?.user?.uid ?? null;

const selectIsImpersonating = (state: StateWithPersonaConfig): boolean =>
  Boolean(state.auth?.isImpersonating);

// The active workspace key — configs are stored per workspace, because
// market assignments and franchise/brand universes are workspace-shaped.
const selectActiveWorkspace = (
  state: StateWithPersonaConfig,
): string | null =>
  state.auth?.selectedWorkspace?.key ?? state.auth?.id_client ?? null;

// The stored config outlives the code that wrote it: it survives logout and
// carries no expiry, so a shape from an older schema or a hand-edited storage
// entry can arrive at readers that index `PERSONA_DEFINITIONS[persona]` and
// `config.markets` without guarding. Those reads throw during render, and a
// config that merely EXISTS also keeps the onboarding gate quiet, so the user
// has no way back. Judging usability here instead turns that into a re-run of
// the wizard, which overwrites the bad entry. The parameter is `unknown`
// because rehydrated storage is untrusted input, whatever the state type says
// it holds.
const isUsablePersonaConfig = (config: unknown): config is PersonaConfig => {
  if (!config || typeof config !== "object") return false;
  const { schemaVersion, persona, markets } = config as Partial<PersonaConfig>;
  if (schemaVersion !== PERSONA_CONFIG_SCHEMA_VERSION) return false;
  // Membership is tested against the list of persona ids rather than with `in`
  // against the definitions map: the map inherits Object.prototype, so `in`
  // accepts "constructor" and "toString" as personas, and the readers then index
  // a definition carrying neither `steps` nor `dashboards` and throw on it.
  if (!persona || !ALL_PERSONA_IDS.includes(persona)) return false;
  return Boolean(markets) && typeof markets === "object";
};

// Per-version upgrades: MIGRATIONS[n] turns a version-n config into a valid
// version-(n+1) one. When you raise PERSONA_CONFIG_SCHEMA_VERSION, register a
// step for every version between the old and new number, or every stored
// config below the new version is treated as unusable and every user who has
// one is sent back through the wizard, losing the persona and per-market
// selections already saved in their Firestore `persona_config.{id_client}`.
// Re-onboarding is the deliberate fallback ONLY for a gap no default can fill
// (a new required field), not the price of any bump — a step that carries the
// old fields forward and defaults the new ones keeps those users out of the
// wizard.
//
// The migration runs at READ time and is not written back: it upgrades the
// object handed to selectors, while the Firestore doc keeps its old
// schemaVersion until the user's next write (finishing the wizard, or an
// add-to-home toggle) PUTs the current-version config. A consumer that reads
// the raw doc rather than going through this module still sees the old shape,
// so a bump that other readers depend on needs a server-side backfill too.
const MIGRATIONS: Record<
  number,
  (config: Record<string, unknown>) => Record<string, unknown>
> = {};

// Brings a stored config up to the current schema by applying each registered
// step in order. Stops and returns the input unchanged when a step is missing,
// leaving isUsablePersonaConfig to reject it. Takes `unknown` because the input
// is rehydrated storage, and returns `unknown` because the result is validated
// downstream rather than trusted here.
// `migrations` is a parameter, defaulting to the registered map, so the
// step-application loop is exercisable in a test without mutating module state.
export const migratePersonaConfig = (
  config: unknown,
  migrations: Record<
    number,
    (config: Record<string, unknown>) => Record<string, unknown>
  > = MIGRATIONS,
): unknown => {
  if (!config || typeof config !== "object") return config;
  let current = config as Record<string, unknown>;
  let version =
    typeof current.schemaVersion === "number" ? current.schemaVersion : 0;
  while (version < PERSONA_CONFIG_SCHEMA_VERSION) {
    const step = migrations[version];
    if (!step) return config;
    current = { ...step(current), schemaVersion: version + 1 };
    version += 1;
  }
  return current;
};

// Dashmote staff default to Power User in every workspace, so the wizard never
// gates them. Staff move between workspaces constantly and hold no single
// persona. A per-workspace wizard would lock them out of a workspace the
// config cannot describe (registry-only, no assigned markets), and would trap
// them when they switch workspace without reloading. This default is a READ,
// never a write: nothing is persisted for staff, so a stored config always
// wins and a staff member who re-runs onboarding for a workspace gets exactly
// what they chose there (Account Profile → Re-run onboarding).
//
// Cached per workspace because callers are `useSelector` selectors: a fresh
// object each read would fail their identity comparison and re-render forever.
const implicitStaffConfigs = new Map<string, PersonaConfig>();

const implicitStaffConfigFor = (workspaceId: string): PersonaConfig => {
  const cached = implicitStaffConfigs.get(workspaceId);
  if (cached) return cached;
  const config: PersonaConfig = {
    schemaVersion: PERSONA_CONFIG_SCHEMA_VERSION,
    persona: "power_user",
    // No wizard ran, and power_user answers no per-market step, so there is
    // nothing to record. `missingMarketsFor` already exempts power_user, so
    // the empty map cannot raise the "set up your view there" banner.
    markets: {},
    completedAt: "",
  };
  implicitStaffConfigs.set(workspaceId, config);
  return config;
};

// Resolves the config for (uid, active workspace): the stored one when there
// is a usable one, else the Dashmote-staff Power User default above. Two
// stored shapes exist: the per-workspace `{ byWorkspace: { ws: PersonaConfig } }`
// written by this code, and the LEGACY flat PersonaConfig from before
// per-workspace storage. A legacy config counts for the workspace it was
// stamped with; an unstamped one is grandfathered into whichever workspace is
// active (it predates the concept, and the first re-run migrates it on write).
export const selectPersonaConfigForUid = (
  state: StateWithPersonaConfig,
  uid: string | null,
): PersonaConfig | null => {
  const stored = selectStoredPersonaConfigForUid(state, uid);
  if (stored) return stored;
  const workspaceId = selectActiveWorkspace(state);
  // Impersonation is excluded on purpose: `state.auth.user` is the TARGET's
  // identity there while `userData` is still the admin's, so the staff default
  // would answer for a client user. The impersonated view keeps its own
  // everything-view fallback (usePersonaVisibility) and Gate 5 skips it anyway.
  if (!workspaceId || selectIsImpersonating(state)) return null;
  return isDashmoteStaffUser(state.auth?.userData)
    ? implicitStaffConfigFor(workspaceId)
    : null;
};

// The one walk over the stored shape, so the two readers below cannot drift.
// They gate different things off the same answer — whether the wizard fires,
// and where a workspace switch lands — and a divergence would either send a
// user through onboarding for a workspace they already set up or strand them
// on one they have not.
//
// Returns the stored object itself, never a copy, so useSelector's identity
// comparison keeps holding across reads.
const readStoredConfig = (
  configsByUid: Record<string, unknown> | undefined,
  uid: string | null,
  workspaceId: string | null,
): PersonaConfig | null => {
  const stored = uid ? (configsByUid?.[uid] ?? null) : null;
  if (!stored || typeof stored !== "object" || !workspaceId) return null;
  const candidate = (
    stored as { byWorkspace?: Record<string, unknown> }
  ).byWorkspace?.[workspaceId];
  return usableConfig(candidate);
};

// Migrate, then judge. Both sources below end here, so a config from the
// session is held to the same shape as one from the server.
//
// Returns the stored object itself, never a copy, so useSelector's identity
// comparison keeps holding across reads.
const usableConfig = (candidate: unknown): PersonaConfig | null => {
  const migrated = migratePersonaConfig(candidate);
  return isUsablePersonaConfig(migrated) ? migrated : null;
};

// Staff read their own session and nothing else. The uid stamp is what makes
// the answer theirs, and skipping `configsByUid` is what makes a row an
// earlier build left on the server unreadable — a staff spot-check ends at
// logout, so a row that outlived one must not decide what they see.
const readStaffSessionConfig = (
  staffSession: StaffPersonaSession | undefined,
  uid: string | null,
  workspaceId: string | null,
): PersonaConfig | null => {
  if (!uid || !workspaceId || staffSession?.uid !== uid) return null;
  return usableConfig(staffSession.byWorkspace?.[workspaceId]);
};

// Whether this identity's answers belong to the session rather than the
// server. Impersonation is excluded for the reason
// `selectPersonaConfigForUid` gives: `userData` is still the admin's there,
// so the predicate would answer for a client user.
const selectIsStaffSessionUser = (state: StateWithPersonaConfig): boolean =>
  !selectIsImpersonating(state) && isDashmoteStaffUser(state.auth?.userData);

// The STORED config, with no staff default behind it. Writers need this
// rather than `selectPersonaConfigForUid`: the staff default is synthesised
// per render and belongs to nobody, so treating it as a base to edit would
// persist a config the user never chose.
const selectStoredPersonaConfigForUid = (
  state: StateWithPersonaConfig,
  uid: string | null,
): PersonaConfig | null => {
  const workspaceId = selectActiveWorkspace(state);
  return selectIsStaffSessionUser(state)
    ? readStaffSessionConfig(state.personaConfig?.staffSession, uid, workspaceId)
    : readStoredConfig(state.personaConfig?.configsByUid, uid, workspaceId);
};

// How far the read of the server has got. Gate 5 needs the tri-state, because
// ABSENCE is its trigger and "not asked yet" is not absence. Collapsing the
// two redirects an onboarded user to the wizard on every load, in the frames
// before the fetch returns.
const selectPersonaLoadStatus = (
  state: StateWithPersonaConfig,
): PersonaLoadStatus => state.personaConfig?.status ?? "idle";

// What the onboarding gate should do, as one answer rather than a boolean the
// caller has to combine with a status. Returning the decision keeps the two
// states that must NOT redirect — still loading, and a resolved read — from
// being re-derived (and mis-derived) at each call site.
//
// A read that ends in error resolves to "onboard": onboarding is mandatory and
// the read (retried with backoff before it reports error) could not confirm a
// config exists, so treating that as "no config" and running the wizard keeps
// every user covered. The cost is benign — the worst a re-run costs an already
// onboarded user is picking Power User again, which is the everything-view they
// already had. During a full backend outage the completing PUT fails too, so
// the user re-runs the wizard until the backend recovers; that self-resolves.
export type PersonaGateDecision = "wait" | "onboard" | "allow";

export const selectPersonaGateDecision = (
  state: StateWithPersonaConfig,
): PersonaGateDecision => {
  if (selectPersonaConfigForUid(state, selectCurrentUid(state)) !== null) {
    return "allow";
  }
  const status = selectPersonaLoadStatus(state);
  if (status === "idle" || status === "loading") return "wait";
  return "onboard";
};

// Whether a NAMED workspace already has a usable config, independent of which
// one is active. The onboarding workspace switcher needs this to decide where
// a switch should land: the app for a workspace already set up, or that
// workspace's own wizard run. The onboarding gate deliberately exempts
// /onboarding from its redirect, so nothing else moves the user off the page.
//
// An unstamped legacy config is grandfathered into the ACTIVE workspace only,
// so it cannot vouch for a different one — that switch gets a wizard run,
// which stamps the config on write.
//
// Dashmote staff answer true for every workspace, because the Power User
// default means none of them needs a wizard run. Without this the switcher
// would leave a staff member on the wizard for a workspace they would in fact
// land on the app in, contradicting the default a few lines above.
export const hasPersonaConfigForWorkspace = (
  state: StateWithPersonaConfig,
  uid: string | null,
  workspaceId: string | null,
): boolean => {
  if (
    workspaceId &&
    !selectIsImpersonating(state) &&
    isDashmoteStaffUser(state.auth?.userData)
  ) {
    return true;
  }
  return (
    readStoredConfig(state.personaConfig?.configsByUid, uid, workspaceId) !==
    null
  );
};

// Markets the user has access to but never configured — e.g. granted after
// onboarding. Surfaced as a non-blocking banner, never a hard gate.
export function missingMarketsFor(
  config: PersonaConfig | null,
  accessibleCountries: readonly string[],
): string[] {
  if (!config) return [];
  // power_user has no per-market steps, so nothing can be "missing".
  if (config.persona === "power_user") return [];
  return accessibleCountries.filter((c) => !(c in config.markets));
}

// Client camelCase → the doc's snake_case. The mirror of the slice's
// `fromServer`, and the only other place the two vocabularies meet.
// `markets` passes through untouched: the `Selection` union is opaque to both
// sides, so neither has to know the other's shape.
const toServerBody = (
  config: PersonaConfig,
  workspaceId: string,
): PersonaConfigBody => ({
  id_client: workspaceId,
  schema_version: config.schemaVersion,
  persona: config.persona,
  premise: config.premise,
  focus: config.focus,
  markets: config.markets,
  completed_at: config.completedAt,
  extras: config.extras,
});

export function usePersonaConfig() {
  const dispatch = useDispatch();
  const uid = useSelector(selectCurrentUid);
  const isImpersonating = useSelector(selectIsImpersonating);
  const workspaceId = useSelector(selectActiveWorkspace);
  const config = useSelector((state: StateWithPersonaConfig) =>
    selectPersonaConfigForUid(state, uid),
  );
  // What `toggleExtra` edits. Deliberately NOT `config`: for a staff member
  // with nothing stored, `config` is the synthesised Power User default, and
  // editing that would PUT a config they never chose while the reducer —
  // which requires a stored entry — silently did nothing.
  const storedConfig = useSelector((state: StateWithPersonaConfig) =>
    selectStoredPersonaConfigForUid(state, uid),
  );
  const isStaffSessionUser = useSelector(selectIsStaffSessionUser);
  const staffWorkspaces = useSelector(
    (state: StateWithPersonaConfig) =>
      state.personaConfig?.staffSession ?? EMPTY_STAFF_PERSONA_SESSION,
  ).byWorkspace;

  // Load this tab's staff answers once per identity, so a refresh keeps the
  // view the staff member is spot-checking. Stamping the uid is what ends
  // the effect: a session with nothing in it still records whose it is.
  const staffSessionUid = useSelector(
    (state: StateWithPersonaConfig) =>
      state.personaConfig?.staffSession?.uid ?? null,
  );
  useEffect(() => {
    if (!isStaffSessionUser || !uid || staffSessionUid === uid) return;
    dispatch(
      setStaffSession({ uid, byWorkspace: readStaffPersonaWorkspaces(uid) }),
    );
  }, [dispatch, isStaffSessionUser, uid, staffSessionUid]);

  // Records a staff member's answers for this session: the slice and the
  // sessionStorage mirror take the same object, and the server is not told.
  const writeStaffSession = useCallback(
    (byWorkspace: StaffPersonaWorkspaces) => {
      if (!uid) return;
      const session = { uid, byWorkspace };
      dispatch(setStaffSession(session));
      writeStaffPersonaSession(session);
    },
    [dispatch, uid],
  );

  // Every write below is refused while impersonating. The identity swap means
  // a write would land on the TARGET's stored preferences, carrying answers
  // the admin gave while looking at a view the target never chose. "View as
  // User" is a read-only lens. Writes also need a resolved workspace, since
  // configs are per workspace.

  // Awaits the server, and reports whether it accepted. The wizard navigates
  // on this boolean: reporting success on a write that did not land would drop
  // the user's answers and send them straight back through the gate with
  // nothing explaining why.
  const setConfig = useCallback(
    async (
      next: Omit<PersonaConfig, "schemaVersion" | "completedAt">,
    ): Promise<boolean> => {
      if (!uid || !workspaceId || isImpersonating) return false;
      const saved: PersonaConfig = {
        ...next,
        schemaVersion: PERSONA_CONFIG_SCHEMA_VERSION,
        completedAt: new Date().toISOString(),
      };
      // A staff run is a spot-check, so it stays in the session and never
      // reaches the server. It cannot fail the way a PUT can, so the wizard
      // is told it landed and navigates.
      if (isStaffSessionUser) {
        writeStaffSession({ ...staffWorkspaces, [workspaceId]: saved });
        return true;
      }
      try {
        await savePersonaConfig(toServerBody(saved, workspaceId));
      } catch (error) {
        console.error("[personaConfig] Failed to save persona config:", error);
        return false;
      }
      dispatch(setPersonaConfig({ uid, workspaceId, config: saved }));
      // The server splits the menus by the stored persona, so they refetch.
      refreshNavigation(uid, workspaceId);
      return true;
    },
    [
      dispatch,
      uid,
      workspaceId,
      isImpersonating,
      isStaffSessionUser,
      staffWorkspaces,
      writeStaffSession,
    ],
  );

  // Home Show all → "Add to home" / "Remove". Widens only the user's own
  // VIEW; entitlement is untouched and rendering still intersects with it.
  //
  // Optimistic, unlike setConfig: this is a one-click toggle on an item the
  // user can see, so applying it immediately and reconciling on the next
  // load's read costs less than a spinner. A failed PUT leaves the toggle
  // applied until the page reloads, then drops it, which is the same
  // best-effort contract favorites already has.
  const toggleExtra = useCallback(
    (kind: ExtraKind, id: string, on: boolean) => {
      if (!uid || !workspaceId || isImpersonating || !storedConfig) return;

      // Same list operation the reducer applies, from its one owner, so the
      // optimistic state and the persisted body cannot diverge.
      const next: PersonaConfig = {
        ...storedConfig,
        extras: nextExtras(storedConfig.extras, kind, id, on),
      };
      // A staff member's pinned cards belong to their spot-check, so they
      // travel with it: same session, same lifetime, no PUT.
      if (isStaffSessionUser) {
        writeStaffSession({ ...staffWorkspaces, [workspaceId]: next });
        return;
      }
      dispatch(toggleExtraAction({ uid, workspaceId, kind, id, on }));
      toggleCachedExtra(uid, workspaceId, kind, id, on);
      savePersonaConfig(toServerBody(next, workspaceId))
        .catch((error) => {
          console.error("[personaConfig] Failed to save add-to-home:", error);
        })
        // Either way the menus refetch, so they show what the server holds.
        .finally(() => refreshNavigation(uid, workspaceId));
    },
    [
      dispatch,
      uid,
      workspaceId,
      isImpersonating,
      storedConfig,
      isStaffSessionUser,
      staffWorkspaces,
      writeStaffSession,
    ],
  );

  // A staff member's answers live only in this session, so the navigation
  // request carries them. A client user's persona is read on the server.
  const sessionPersonaConfig = useMemo(
    () =>
      isStaffSessionUser && storedConfig && workspaceId
        ? toServerBody(storedConfig, workspaceId)
        : null,
    [isStaffSessionUser, storedConfig, workspaceId],
  );

  return useMemo(
    () => ({
      uid,
      config,
      sessionPersonaConfig,
      // Whether there is a config to personalise. False for the Dashmote-staff
      // Power User default, which is synthesised per read and owned by nobody:
      // `toggleExtra` refuses on it, so a surface that offered the control
      // anyway would render a button that does nothing.
      canPersonalize: storedConfig !== null && !isImpersonating,
      setConfig,
      toggleExtra,
    }),
    [uid, config, sessionPersonaConfig, storedConfig, isImpersonating, setConfig, toggleExtra],
  );
}
